import axios, { AxiosInstance } from 'axios';

import { environment } from '@app/environments/environment';

export abstract class CmsServiceBase {
    protected axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: environment.CMS_API_ENDPOINT,
            headers: {
                'Content-Type': 'application/json',
                ...(environment.CMS_API_TOKEN && {
                    Authorization: `Bearer ${environment.CMS_API_TOKEN}`,
                }),
            },
        });
    }

    /**
     * Build Strapi query parameters for filtering, sorting, pagination, and population
     * @protected
     */
    protected buildStrapiParams(options: {
        filters?: Record<string, any>;
        sort?: string | string[];
        pagination?: { limit?: number; page?: number };
        status?: 'draft' | 'published';
        populate?: string | string[] | Record<string, any>;
    }): Record<string, any> {
        const params: Record<string, any> = {};

        if (options.filters) {
            Object.entries(options.filters).forEach(([key, value]) => {
                if (typeof value === 'object' && value !== null) {
                    Object.entries(value).forEach(([operator, val]) => {
                        params[`filters[${key}][${operator}]`] = val;
                    });
                } else {
                    params[`filters[${key}]`] = value;
                }
            });
        }

        if (options.sort) {
            params.sort = Array.isArray(options.sort) ? options.sort.join(',') : options.sort;
        }

        if (options.pagination) {
            if (options.pagination.limit !== undefined) {
                params['pagination[limit]'] = options.pagination.limit;
            }
            if (options.pagination.page !== undefined) {
                params['pagination[page]'] = options.pagination.page;
            }
        }

        if (options.status) {
            params.status = options.status;
        }

        if (options.populate) {
            if (Array.isArray(options.populate)) {
                params.populate = options.populate.join(',');
            } else if (typeof options.populate === 'string') {
                params.populate = options.populate;
            } else if (typeof options.populate === 'object') {
                this.buildPopulateParams(options.populate, params, 'populate');
            } else {
                params.populate = '*';
            }
        }

        return params;
    }

    /**
     * Build nested populate parameters for Strapi deep population
     * @private
     */
    private buildPopulateParams(
        populateObj: Record<string, any>,
        params: Record<string, any>,
        prefix: string,
    ): void {
        Object.entries(populateObj).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                params[`${prefix}[${key}]`] = value.join(',');
            } else if (typeof value === 'object' && value !== null) {
                if (value.populate) {
                    this.buildPopulateParams(value.populate, params, `${prefix}[${key}][populate]`);
                } else {
                    this.buildPopulateParams(value, params, `${prefix}[${key}]`);
                }
            } else {
                params[`${prefix}[${key}]`] = value;
            }
        });
    }
}
